import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Layers,
  Monitor,
  Sparkles,
  Layout,
  Crown,
} from "lucide-react";
import type {
  ThemeKey,
  FontPairing,
  BackgroundStyle,
  AnimationLevel,
  LayoutDensity,
  CornerRadius,
  SectionArrangement,
} from "@profolio/validators";

import type { ThemeEffects } from "@profolio/templates";

export interface PresetConfig {
  themeKey: ThemeKey;
  colorPalette: string;
  fontPairing: FontPairing;
  backgroundStyle: BackgroundStyle;
  animationLevel: AnimationLevel;
  layoutDensity: LayoutDensity;
  cornerRadius: CornerRadius;
  sectionArrangement: SectionArrangement;
}

export interface PresetRecipe {
  id: string;
  name: string;
  description: string;
  isPro: boolean;
  icon: LucideIcon;
  config: PresetConfig;
  effects: ThemeEffects;
  contentMaxWidth?: number;
}

export type PresetItem = PresetRecipe;

// ─── 6 Canonical Flagship Art Direction Recipes (Phase M4) ────────────────────
export const CANONICAL_PRESETS: PresetRecipe[] = [
  {
    id: "obsidian-executive",
    name: "Obsidian Executive",
    description: "Deep dark graphite canvas with champagne gold and subtle depth.",
    isPro: true,
    icon: Crown,
    config: {
      themeKey: "modern-executive",
      colorPalette: "obsidian-executive",
      fontPairing: "modern-sans",
      backgroundStyle: "solid",
      animationLevel: "subtle",
      layoutDensity: "standard",
      cornerRadius: "subtle",
      sectionArrangement: "linear",
    },
    effects: {
      level: "subtle",
      glass: true,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    contentMaxWidth: 920,
  },
  {
    id: "sapphire-scholar",
    name: "Sapphire Scholar",
    description: "Classical academic dignity on alabaster paper, hanging indent citations, and formal Oxford navy typography.",
    isPro: false,
    icon: BookOpen,
    config: {
      themeKey: "minimalist-scholar",
      colorPalette: "sapphire-scholar",
      fontPairing: "serif-classic",
      backgroundStyle: "solid",
      animationLevel: "subtle",
      layoutDensity: "standard",
      cornerRadius: "subtle",
      sectionArrangement: "linear",
    },
    effects: {
      level: "subtle",
      glass: false,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    contentMaxWidth: 760,
  },
  {
    id: "porcelain-swiss",
    name: "Porcelain Swiss",
    description: "International Typographic Style: pure flat monochrome, 0px sharp geometry, and strict architectural whitespace.",
    isPro: false,
    icon: Monitor,
    config: {
      themeKey: "minimalist-scholar",
      colorPalette: "minimal-monochrome",
      fontPairing: "modern-sans",
      backgroundStyle: "solid",
      animationLevel: "subtle",
      layoutDensity: "relaxed",
      cornerRadius: "none",
      sectionArrangement: "linear",
    },
    effects: {
      level: "none",
      glass: false,
      glow: false,
      hoverLift: false,
      ambientBackground: false,
    },
    contentMaxWidth: 800,
  },
  {
    id: "editorial-atelier",
    name: "Editorial Atelier",
    description: "Warm natural linen monograph with terracotta accents, deep espresso text, literary drop caps, and magazine pacing.",
    isPro: true,
    icon: Sparkles,
    config: {
      themeKey: "classic-academic",
      colorPalette: "oxford-navy",
      fontPairing: "editorial",
      backgroundStyle: "solid",
      animationLevel: "subtle",
      layoutDensity: "standard",
      cornerRadius: "subtle",
      sectionArrangement: "linear",
    },
    effects: {
      level: "subtle",
      glass: false,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    contentMaxWidth: 960,
  },
  {
    id: "cyber-research",
    name: "Cyber Research",
    description: "High-density technical card matrix with luminous cyan accents and structured tiles.",
    isPro: true,
    icon: Layout,
    config: {
      themeKey: "modern-bento",
      colorPalette: "cyber-teal",
      fontPairing: "technical",
      backgroundStyle: "geometric-pattern",
      animationLevel: "subtle",
      layoutDensity: "compact",
      cornerRadius: "subtle",
      sectionArrangement: "bento",
    },
    effects: {
      level: "expressive",
      glass: true,
      glow: true,
      hoverLift: true,
      ambientBackground: true,
    },
    contentMaxWidth: 1040,
  },
  {
    id: "aurora-showcase",
    name: "Aurora Showcase",
    description: "Vibrant visual portfolio layout with subtle ambient aurora mesh and iridescent badges.",
    isPro: true,
    icon: Layers,
    config: {
      themeKey: "grid-showcase",
      colorPalette: "aurora",
      fontPairing: "modern-sans",
      backgroundStyle: "animated-gradient",
      animationLevel: "moderate",
      layoutDensity: "standard",
      cornerRadius: "rounded",
      sectionArrangement: "bento",
    },
    effects: {
      level: "expressive",
      glass: true,
      glow: false,
      hoverLift: true,
      ambientBackground: true,
    },
    contentMaxWidth: 1080,
  },
];

// ─── 8 Legacy Visual Presets (Preserved for backward compatibility) ───────────
export const LEGACY_PRESETS: PresetRecipe[] = [
  {
    id: "classic-academic",
    name: "Classic Academic",
    description: "Traditional Oxford-style serif with dignified navy accents.",
    isPro: false,
    icon: BookOpen,
    config: {
      themeKey: "classic-academic",
      colorPalette: "ocean-blue",
      fontPairing: "serif-classic",
      backgroundStyle: "solid",
      animationLevel: "subtle",
      layoutDensity: "standard",
      cornerRadius: "subtle",
      sectionArrangement: "linear",
    },
    effects: {
      level: "subtle",
      glass: false,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    contentMaxWidth: 960,
  },
  {
    id: "modern-bento",
    name: "Modern Bento",
    description: "High-impact visual card matrix with glassmorphism and aurora glow.",
    isPro: true,
    icon: Layers,
    config: {
      themeKey: "modern-bento",
      colorPalette: "aurora",
      fontPairing: "modern-sans",
      backgroundStyle: "animated-gradient",
      animationLevel: "subtle",
      layoutDensity: "standard",
      cornerRadius: "rounded",
      sectionArrangement: "bento",
    },
    effects: {
      level: "expressive",
      glass: true,
      glow: false,
      hoverLift: true,
      ambientBackground: true,
    },
    contentMaxWidth: 1040,
  },
  {
    id: "swiss-minimalist",
    name: "Swiss Minimal",
    description: "Ultra-clean monochrome typography with generous whitespace.",
    isPro: false,
    icon: Monitor,
    config: {
      themeKey: "minimalist-scholar",
      colorPalette: "minimal-monochrome",
      fontPairing: "modern-sans",
      backgroundStyle: "solid",
      animationLevel: "subtle",
      layoutDensity: "relaxed",
      cornerRadius: "none",
      sectionArrangement: "linear",
    },
    effects: {
      level: "none",
      glass: false,
      glow: false,
      hoverLift: false,
      ambientBackground: false,
    },
    contentMaxWidth: 760,
  },
  {
    id: "executive-slate",
    name: "Executive Slate",
    description: "Deep dark graphite canvas with warm gold and champagne highlights.",
    isPro: true,
    icon: Crown,
    config: {
      themeKey: "modern-executive",
      colorPalette: "graphite-luxe",
      fontPairing: "editorial",
      backgroundStyle: "gradient-linear",
      animationLevel: "subtle",
      layoutDensity: "standard",
      cornerRadius: "subtle",
      sectionArrangement: "linear",
    },
    effects: {
      level: "subtle",
      glass: true,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    contentMaxWidth: 920,
  },
  {
    id: "research-lab",
    name: "Research Lab",
    description: "High-density technical layout with cyan accents and structured cards.",
    isPro: true,
    icon: Layout,
    config: {
      themeKey: "modern-bento",
      colorPalette: "coastal",
      fontPairing: "technical-mono",
      backgroundStyle: "geometric-pattern",
      animationLevel: "subtle",
      layoutDensity: "compact",
      cornerRadius: "subtle",
      sectionArrangement: "bento",
    },
    effects: {
      level: "subtle",
      glass: true,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    contentMaxWidth: 1040,
  },
  {
    id: "quiet-luxury",
    name: "Quiet Luxury",
    description: "Warm amber background with editorial serif and soft muted taupe.",
    isPro: true,
    icon: Sparkles,
    config: {
      themeKey: "classic-academic",
      colorPalette: "amber-gold",
      fontPairing: "editorial",
      backgroundStyle: "solid",
      animationLevel: "subtle",
      layoutDensity: "relaxed",
      cornerRadius: "subtle",
      sectionArrangement: "linear",
    },
    effects: {
      level: "subtle",
      glass: false,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    contentMaxWidth: 960,
  },
  {
    id: "creative-grid",
    name: "Creative Grid",
    description: "Vibrant saffron and amber tones in an expressive grid showcase.",
    isPro: true,
    icon: Layers,
    config: {
      themeKey: "grid-showcase",
      colorPalette: "saffron",
      fontPairing: "modern-sans",
      backgroundStyle: "animated-gradient",
      animationLevel: "moderate",
      layoutDensity: "standard",
      cornerRadius: "rounded",
      sectionArrangement: "bento",
    },
    effects: {
      level: "expressive",
      glass: true,
      glow: false,
      hoverLift: true,
      ambientBackground: true,
    },
    contentMaxWidth: 1080,
  },
  {
    id: "collegiate-royal",
    name: "Collegiate Royal",
    description: "Dignified Oxford navy and gold with traditional serif typography.",
    isPro: false,
    icon: BookOpen,
    config: {
      themeKey: "classic-academic",
      colorPalette: "oxford-navy",
      fontPairing: "serif-classic",
      backgroundStyle: "solid",
      animationLevel: "subtle",
      layoutDensity: "standard",
      cornerRadius: "subtle",
      sectionArrangement: "linear",
    },
    effects: {
      level: "subtle",
      glass: false,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    contentMaxWidth: 960,
  },
];

// Re-export PRESETS as LEGACY_PRESETS for 100% backward compatibility
export const PRESETS: PresetRecipe[] = LEGACY_PRESETS;

export function resolvePreset(id?: string): PresetRecipe {
  if (!id) return CANONICAL_PRESETS[1]; // default: Sapphire Scholar
  const canonical = CANONICAL_PRESETS.find((p) => p.id === id);
  if (canonical) return canonical;
  const legacy = LEGACY_PRESETS.find((p) => p.id === id);
  if (legacy) return legacy;
  return CANONICAL_PRESETS[1];
}

// ─── Tier Capability Helpers ────────────────────────────────────────────────
export const FREE_THEMES: readonly ThemeKey[] = ["classic-academic", "minimalist-scholar"];
export const FREE_FONTS: readonly FontPairing[] = ["serif-classic", "modern-sans"];
export const FREE_PALETTES: readonly string[] = [
  "ocean-blue",
  "oxford-navy",
  "emerald-forest",
  "royal-amethyst",
  "crimson-slate",
  "amber-gold",
  "midnight-dark",
  "minimal-monochrome",
];
export const FREE_BACKGROUNDS: readonly BackgroundStyle[] = ["solid"];
export const FREE_ANIMATIONS: readonly AnimationLevel[] = ["none", "subtle"];

export function isPresetPro(preset: PresetItem): boolean {
  return preset.isPro;
}

export function isPalettePro(paletteId: string): boolean {
  return !FREE_PALETTES.includes(paletteId);
}

export function isFontPro(fontId: FontPairing): boolean {
  return !FREE_FONTS.includes(fontId);
}

export function isBackgroundPro(bgId: BackgroundStyle): boolean {
  return !FREE_BACKGROUNDS.includes(bgId);
}

export function isAnimationPro(animId: AnimationLevel): boolean {
  return !FREE_ANIMATIONS.includes(animId);
}

export function isThemePro(themeKey: ThemeKey): boolean {
  return !FREE_THEMES.includes(themeKey);
}
